import React, { createContext, useState, useEffect, ReactNode } from 'react';

// Define the available languages
export type Language = 'he' | 'en' | 'fr' | 'es';

// Define the context interface
interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  direction: 'rtl' | 'ltr';
}

// Create the context with a default value
export const LanguageContext = createContext<LanguageContextType>({
  language: 'he',
  setLanguage: () => {},
  direction: 'rtl',
});

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  // Initialize state from localStorage or browser language preference
  const [language, setLanguageState] = useState<Language>(() => {
    // First, try to get from localStorage
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && ['he', 'en', 'fr', 'es'].includes(savedLanguage)) {
      return savedLanguage as Language;
    }

    // Otherwise, try to detect from browser
    const browserLang = navigator.language.split('-')[0];
    
    // Default to Hebrew unless browser is set to one of our supported languages
    if (['en', 'fr', 'es'].includes(browserLang)) {
      return browserLang as Language;
    }
    
    return 'he'; // Default to Hebrew
  });

  // Determine the direction based on the language
  const direction = language === 'he' ? 'rtl' : 'ltr';

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem('language', language);
    // Set the direction attribute on the document for RTL support
    document.documentElement.dir = direction;
    document.documentElement.lang = language;
  }, [language, direction]);

  // Set language function
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, direction }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for using the language context
export const useLanguage = () => {
  const context = React.useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 